import React, {
  CSSProperties,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import {
  Tabs,
  Grid,
  Card,
  Button,
  TextInput,
  Checkbox,
  BadgeColor,
  Icons,
  IconButton,
  StatusChip,
} from "@pushpress/shared-components";
import { useHistory } from "react-router-dom";
import Dashboard from "../../layouts/Dashboard";
import Robot from "../../assets/img/icons/robot.svg";
import { AppContext } from "../../context/app";
import ContentLoading from "../../components/ContentLoading";
import { checkGoToOlderSettingsPage, hasAppAccess } from "./utils";
import {
  IAppsFields,
  ICategoriesFields,
  ITypesFields,
} from "../../types/appstore";
import { useAppStoreAppsCategoriesAndTypes } from "../../apis/appStore.hooks";
import { ObjectToBeDefined } from "../../types/types";
import { useAnalytics } from "../../components/providers/AnalyticsProvider";
import { CommittedClubEvent } from "../CommittedClub/CommittedClubEvents";

const tabHeaders = ["Discover Apps", "My Apps"];
const styles = {
  appCard: {
    border: "none",
    overflow: "inherit",
    minHeight: "360px",
  },
  appImage: {
    width: "100%",
    objectFit: "cover",
    borderTopLeftRadius: "10px",
    borderTopRightRadius: "10px",
  },
  appContent: {
    minHeight: "160px",
  },
  noShowText: {
    marginTop: "16px",
    marginBottom: "22px",
  },
} as {
  [name: string]: CSSProperties;
};

const oldV2AppPage = (slug: string) => `/v2/integrations/app/${slug}`;
const oldAppPage = (slug: string) => `/integrations/app/${slug}`;
const newAppPage = (slug: string) => `/app-store/${slug}`;
const accountSettingsPage = `/settings/account`;

const hrefUrl = (app: ObjectToBeDefined) => {
  if (app.isInstalled && checkGoToOlderSettingsPage(app)) {
    if (app.isLegacy) {
      return oldAppPage(app.slug);
    } else {
      return oldV2AppPage(app.slug);
    }
  } else {
    return newAppPage(app.slug);
  }
};

const App = () => {
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const { client } = useContext(AppContext);
  const [searchValue, setSearchValue] = useState("");
  const [typeFilter, setTypeFilters] = useState({});
  const [categoryFilter, setCategoryFilter] = useState({});
  const history = useHistory();

  const { trackEvent } = useAnalytics();

  // Get apps
  const { isLoading, data } = useAppStoreAppsCategoriesAndTypes();
  const { apps, categories, types } = data || {};

  const { myApps, filteredApps } = useMemo(() => {
    if (!apps || !types || !categories) {
      return {
        myApps: [],
        filteredApps: [],
      };
    }

    const hasTypeFilters = Object.values(typeFilter).some((val) => !!val);
    const hasCategoryFilters = Object.values(categoryFilter).some(
      (val) => !!val,
    );

    // Filter by search value
    let filteredApps = apps.filter((app) =>
      app.name.toUpperCase().includes(searchValue.toUpperCase()),
    );

    // Filter by types
    if (hasTypeFilters) {
      filteredApps = filteredApps.filter((app) =>
        (app.types as ITypesFields[]).find((type) => typeFilter[type.id]),
      );
    }

    // Filter by categories
    if (hasCategoryFilters) {
      filteredApps = filteredApps.filter((app) =>
        (app.categories as ICategoriesFields[]).find(
          (category) => categoryFilter[category.id],
        ),
      );
    }

    const myApps = apps.filter((app) => app.isInstalled);

    return {
      myApps,
      filteredApps,
    };
  }, [apps, typeFilter, categoryFilter, searchValue, categories, types]);

  const filters = useMemo(() => {
    return (
      <Card>
        <Card.Content>
          <Grid container direction="column" columnSpacing={4}>
            <Grid item xs={12}>
              <h3>Filter</h3>
              <Grid container columnSpacing={0} paddingY={1}>
                <Grid item xs={12}>
                  <label htmlFor="search-app">Search</label>
                  <TextInput
                    name="search-app"
                    value={searchValue}
                    onValueChange={setSearchValue}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <h4>Type</h4>
              <Grid container spacing={1} columnSpacing={1} paddingY={1}>
                {types?.map((type, index) => (
                  <React.Fragment key={index}>
                    <Grid item xs={10}>
                      <Checkbox
                        label={type.name}
                        onToggle={() => {
                          setTypeFilters({
                            ...typeFilter,
                            [type.id]: !typeFilter[type.id],
                          });
                        }}
                      />
                    </Grid>
                    <Grid item xs={2}>
                      <StatusChip
                        label={String(
                          apps?.reduce((acc, app) => {
                            return (
                              acc +
                              ((app.types as ITypesFields[]).find(
                                (appType) => appType.id === type.id,
                              )
                                ? 1
                                : 0)
                            );
                          }, 0),
                        )}
                        color={BadgeColor.Success}
                      />
                    </Grid>
                  </React.Fragment>
                ))}
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <h4>Category</h4>
              <Grid container spacing={1} columnSpacing={1} paddingY={1}>
                {categories?.map((category, index) => (
                  <React.Fragment key={index}>
                    <Grid item xs={10}>
                      <Checkbox
                        label={category.name}
                        onToggle={() => {
                          setCategoryFilter({
                            ...categoryFilter,
                            [category.id]: !categoryFilter[category.id],
                          });
                        }}
                      />
                    </Grid>
                    <Grid item xs={1}>
                      <StatusChip
                        label={String(
                          apps?.reduce((acc, app) => {
                            return (
                              acc +
                              ((app.categories as ICategoriesFields[]).find(
                                (appCategory) => appCategory.id === category.id,
                              )
                                ? 1
                                : 0)
                            );
                          }, 0),
                        )}
                        color={BadgeColor.Success}
                      />
                    </Grid>
                  </React.Fragment>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Card.Content>
      </Card>
    );
  }, [apps, searchValue, categories, types, typeFilter, categoryFilter]);

  const CardApp = useCallback(
    ({ app }: { app: IAppsFields }) => {
      const trackCommittedClubClick = () => {
        trackEvent(CommittedClubEvent.ConfigureSelected, {
          installed_now: app.isInstalled,
          app: "committed_club",
        });
      };

      return (
        <Grid item xs={12} sm={6} md={6} lg={4} xl={3}>
          <Card style={styles.appCard}>
            <img
              src={app.image.fields.file.url}
              alt={app.name}
              style={styles.appImage}
            />
            <Card.Content style={styles.appContent}>
              <Grid container>
                <Grid item xs={12}>
                  <Grid container justifyContent="space-between">
                    <Grid item xs={app.beta ? 9 : 12}>
                      <h3>{app.name}</h3>
                    </Grid>
                    {app.beta && (
                      <Grid item xs={3}>
                        <StatusChip label="BETA" color={BadgeColor.White} />
                      </Grid>
                    )}
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  <p>{app.summary}</p>
                </Grid>
              </Grid>
            </Card.Content>
            <Card.Footer>
              <Grid container>
                <Grid item xs={6}>
                  <Button
                    buttonType="secondary"
                    text={app.isInstalled ? "Configure" : "Learn more"}
                    href={hrefUrl(app)}
                    onClick={() => {
                      if (app.settingComponent === "CommittedClub") {
                        trackCommittedClubClick();
                      }

                      if (app.isInstalled && checkGoToOlderSettingsPage(app)) {
                        if (app.isLegacy) {
                          window.location.href = hrefUrl(app);
                        } else {
                          window.location.href = hrefUrl(app);
                        }
                      } else {
                        history.push(hrefUrl(app));
                      }
                    }}
                  />
                </Grid>
                {(app.proOnly || app.maxOnly) && (
                  <Grid item xs={6}>
                    <Grid
                      container
                      justifyContent={"flex-end"}
                      alignItems={"center"}
                    >
                      <Grid item>
                        {hasAppAccess(app, client) ? (
                          <IconButton
                            icon={Icons.Unlock}
                            tooltip={`Available with your ${client.planType} plan`}
                            onClick={() => {
                              history.push(newAppPage(app.slug));
                            }}
                          />
                        ) : (
                          <IconButton
                            icon={Icons.Lock}
                            tooltip={"Requires Pro or Max Plan"}
                            onClick={() => {
                              window.location.href = accountSettingsPage;
                            }}
                          />
                        )}
                      </Grid>
                    </Grid>
                  </Grid>
                )}
              </Grid>
            </Card.Footer>
          </Card>
        </Grid>
      );
    },
    [client, history, trackEvent],
  );

  const noShowMyApps = useMemo(() => {
    return (
      <Grid
        container
        item
        xs={12}
        direction="column"
        justifyContent="center"
        alignItems="center"
      >
        <Robot />
        <h2 style={styles.noShowText}>No Apps Connected</h2>
        <Button
          buttonType="primary"
          text="Discover Apps"
          onClick={() => {
            setSelectedTabIndex(0);
          }}
        />
      </Grid>
    );
  }, []);

  const noShowDiscoverApps = useMemo(() => {
    return (
      <Grid
        container
        item
        xs={12}
        direction="column"
        justifyContent="center"
        alignItems="center"
      >
        <Robot />
        <h2 style={styles.noShowText}>No Apps To Discover</h2>
        <Button
          buttonType="primary"
          text="My Apps"
          onClick={() => {
            setSelectedTabIndex(1);
          }}
        />
      </Grid>
    );
  }, []);

  if (isLoading) {
    return <ContentLoading />;
  }

  return (
    <Dashboard>
      <h1>App Store</h1>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Tabs
            tabHeaders={tabHeaders}
            selectedIndex={selectedTabIndex}
            changeTab={setSelectedTabIndex}
            onTabClick={setSelectedTabIndex}
          >
            <Grid container spacing={3} marginY={5}>
              <Grid item xs={12} sm={4} md={3} lg={3} xl={2}>
                {filters}
              </Grid>
              <Grid item xs={12} sm={8} md={9} lg={9} xl={10}>
                <Grid container columnSpacing={5} spacing={3}>
                  {filteredApps.length > 0
                    ? filteredApps.map((app, index) => (
                        <React.Fragment key={index}>
                          <CardApp app={app} />
                        </React.Fragment>
                      ))
                    : noShowDiscoverApps}
                </Grid>
              </Grid>
            </Grid>
            <Grid container spacing={3} marginY={5}>
              <Grid item xs={12}>
                <Grid container columnSpacing={5} spacing={3}>
                  {myApps.length > 0
                    ? myApps.map((app, index) => (
                        <React.Fragment key={index}>
                          <CardApp app={app} />
                        </React.Fragment>
                      ))
                    : noShowMyApps}
                </Grid>
              </Grid>
            </Grid>
          </Tabs>
        </Grid>
      </Grid>
    </Dashboard>
  );
};

export default App;
